import React from "react";
import { Box, Icon, ResponsiveLayout, Typography } from "@bca/ui-library";

import VINSVG from "./Icons/VINSVG";
import ServiceHistory from "./ServiceHistory";
import FuelSVG from "../components/Icons/FuelSVG";
import DoorSVG from "../components/Icons/DoorSVG";
import EquipmentSection from "./EquipmentSection";
import COEmissionsSVG from "./Icons/COEmissionsSVG";
import NOXEmissionsSVG from "./Icons/NOXEmissionsSVG";
import ColorDisc from "../components/Icons/ColorDisc";
import OwnersSVG from "../components/Icons/OwnersSVG";
import { formatDate } from "../helpers/date.functions";
import LogBookSVG from "../components/Icons/LogBookSVG";
import VatTypeSVG from "../components/Icons/VatTypeSVG";
import CalendarSVG from "../components/Icons/CalendarSVG";
import { DeclarationSection } from "./DeclarationSection";
import TransmissionSVG from "../components/Icons/TransmissionSVG";
import VehicleTypes, { VehicleType } from "../types/vehicleTypes";
import VehicleTypeSvgMapper from "./constants/VehicleTypeSvgMapper";
import {
  Item,
  InsuranceTotalLoss,
} from "../vehicleDetails/vehicle-details.type";
import { shallowEqual, useSelector } from "react-redux";
import AdditionalInfoSection from "./AdditionalInfoSection";
import { useViewport } from "../hooks/useViewport";
import { useFeatureFlags } from "bca-feature-flag-provider";
import { StoreType } from "../redux/StoreType";
import { GET_APP_STATE } from "../redux/slices/appSlice";

const colorMapper = {
  black: "#000000",
  grey: "#808080",
  white: "#ffffff",
  blue: "#0000ff",
  silver: "#c0c0c0",
  red: "#ff0000",
  green: "#008000",
  brown: "#6d1d1d",
  gold: "#ffd700",
  orange: "#ffa500",
  beige: "#f5f5dc",
  yellow: "#ffff00",
  mauve: "#e0b0ff",
  violet: "#ee82ee",
  purple: "#800080",
  cream: "#fffdd0",
  burgundy: "#800020",
  pink: "#ff79e2",
};

interface SpecificationProps {
  doors: string;
  fuel: string;
  keys?: number;
  color: string;
  transmission: string;
  co2Emissions: string;
  noxEmissions: string;
  vehicleType: VehicleTypes;
  manufacturerColour: string;
}

const Specification = ({
  keys,
  fuel,
  color,
  doors,
  vehicleType,
  transmission,
  co2Emissions,
  noxEmissions,
  manufacturerColour,
}: SpecificationProps) => {
  const numberOfDoors = vehicleType !== VehicleType.MOTORCYCLE && (
    <SpecificationItem label="No. of doors" value={doors}>
      <DoorSVG width={18} height={15} marginX={0} display="inline-flex;" />
    </SpecificationItem>
  );

  return (
    <Box paddingX={0} marginBottom="44px" marginTop={[9, 0]}>
      <Typography variant="h5" css="margin: 0 0 24px 0;">
        Specification
      </Typography>
      <Box
        display="flex"
        flexWrap="wrap"
        justifyContent="flex-start"
        style={{ columnGap: "20px" }}
      >
        <SpecificationItem label="Vehicle Type" value={vehicleType}>
          {VehicleTypeSvgMapper[vehicleType]}
        </SpecificationItem>
        <SpecificationItem label="Colour" value={manufacturerColour}>
          <ColorDisc background={colorMapper[color] || color} />
        </SpecificationItem>
        <SpecificationItem label="Fuel" value={fuel}>
          <FuelSVG width={18} height={18} marginX={0} display="inline-flex;" />
        </SpecificationItem>
        <SpecificationItem label="Transmission" value={transmission}>
          <TransmissionSVG
            width={18}
            height={18}
            marginX={0}
            display="inline-flex;"
          />
        </SpecificationItem>
        {numberOfDoors}
        <SpecificationItem
          label="CO2 Emissions"
          value={Boolean(co2Emissions) ? co2Emissions : "-"}
        >
          <COEmissionsSVG
            width={18}
            height={15}
            marginX={0}
            display="inline-flex;"
          />
        </SpecificationItem>
        <SpecificationItem
          label="NOX Emissions"
          value={Boolean(noxEmissions) ? noxEmissions : "-"}
        >
          <NOXEmissionsSVG
            width={18}
            height={15}
            marginX={0}
            display="inline-flex;"
          />
        </SpecificationItem>
        <SpecificationItem
          label="No. of keys"
          value={Boolean(keys) ? keys?.toString() : "-"}
        >
          <Icon
            name="key"
            weight="solid"
            width="18px"
            height="18px"
            color="#070B16"
          />
        </SpecificationItem>
      </Box>
    </Box>
  );
};

interface OwnershipProps {
  vin: string;
  owners: string;
  logBook: string;
  vatType: string;
  registrationDate: string;
}

const Ownership = ({
  vin,
  owners,
  logBook,
  vatType,
  registrationDate,
}: OwnershipProps) => {
  const {
    app: { isVendor },
  }: StoreType = useSelector(GET_APP_STATE, shallowEqual);

  const viewportWidth = useViewport(100);
  const isMobileDevice = viewportWidth === "mobile";

  return (
    <Box
      paddingX={0}
      marginBottom={{ mobile: "16px", tablet: "44px", desktop: "44px" }}
      marginTop={[-7, -7, 0]}
    >
      <Typography variant="h5" css="margin: 0 0 24px 0;">
        Ownership
      </Typography>
      <Box display="flex" flexWrap="wrap" style={{ columnGap: "20px" }}>
        <SpecificationItem label="Log book" value={logBook}>
          <LogBookSVG
            width={16}
            height={18}
            marginX={0}
            display="inline-flex;"
          />
        </SpecificationItem>
        <SpecificationItem label="No. of owners" value={owners}>
          <OwnersSVG
            width={20}
            height={14}
            marginX={0}
            display="inline-flex;"
          />
        </SpecificationItem>
        <SpecificationItem
          label="Date of registration"
          value={registrationDate}
        >
          <CalendarSVG
            width={16}
            height={18}
            marginX={0}
            display="inline-flex;"
          />
        </SpecificationItem>
        <SpecificationItem label="VAT Type" value={vatType}>
          <VatTypeSVG
            width={16}
            height={18}
            marginX={0}
            display="inline-flex;"
          />
        </SpecificationItem>
        {isVendor && (
          <SpecificationItem
            label={`VIN ${isMobileDevice ? "Number" : ""}`}
            value={vin}
          >
            <VINSVG width={20} height={18} marginX={0} display="inline-flex;" />
          </SpecificationItem>
        )}
      </Box>
    </Box>
  );
};

interface SpecificationItemProps {
  label: string;
  value: string;
  color?: string;
  fullWidth?: boolean;
  height?: string | number;
  children: React.ReactElement;
}

export const SpecificationItem = ({
  label,
  value,
  children,
  height = 19,
  color = "grey-blue",
  fullWidth = false,
}: SpecificationItemProps) => {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const getWidth = () => {
    if (fullWidth) {
      return "100%";
    }

    return ["50%", "20%", "20%", "20%"];
  };

  return (
    <Box marginBottom={[7, 7, 6]}>
      <Typography
        variant="body"
        color={color}
        css={`
          margin: 0 0 11px 0;
          line-height: 1.36;
        `}
      >
        {label}
      </Typography>
      <Box
        flex={1}
        width={210}
        minWidth={0}
        display="flex"
        height={height}
        alignItems="baseline"
      >
        {children}
        <Typography
          variant="body"
          css={`
            display: inline-flex;
            margin: 0;
            margin-left: 8px;
            line-height: 1.36;
            font-weight: 500;
            align-self: flex-start;
          `}
        >
          {value}
        </Typography>
      </Box>
    </Box>
  );
};

interface MechanicalDeclarationType {
  typeCode: string;
  type: string;
  valueCode: string;
  value: string;
}

export interface DeclarationType {
  mechanical?: MechanicalDeclarationType[];
  usedAsTaxi?: boolean;
  majorDamage?: boolean;
  reregistered?: boolean;
  ownedByPolice?: boolean;
  modifiedVehicle?: boolean;
  changeOfRegistration?: boolean;
}

interface VehicleSpecificationProps {
  vin: string;
  fuel: string;
  color: string;
  doors: string;
  notes?: string;
  owners: string;
  logBook: string;
  vatType: string;
  dvsaMileage?: any;
  transmission: string;
  co2Emissions?: string;
  numberOfKeys?: number;
  noxEmissions?: string;
  MOTExpiryDate: string;
  serviceHistory: string;
  advisoryItems?: Item[];
  equipmentItems?: Item[];
  lastServiceDate: string;
  registrationDate: string;
  vehicleType: VehicleTypes;
  declaration?: DeclarationType;
  manufacturerColour: string;
  lastServiceMileage?: number;
  insuranceTotalLoss?: InsuranceTotalLoss[];
  serviceCount?: { total: number; mainDealer: number };
}

const VehicleSpecification: React.FunctionComponent<
  VehicleSpecificationProps
> = ({
  vin,
  fuel,
  notes,
  color,
  doors,
  owners,
  logBook,
  vatType,
  vehicleType,
  declaration,
  dvsaMileage,
  transmission,
  serviceCount,
  numberOfKeys,
  co2Emissions,
  noxEmissions,
  MOTExpiryDate,
  advisoryItems,
  equipmentItems,
  serviceHistory,
  lastServiceDate,
  registrationDate,
  manufacturerColour,
  lastServiceMileage,
  insuranceTotalLoss,
}) => {
  let dvsaString = "";

  const getComma = (index) => {
    if (dvsaMileage.length - 1 === index) {
      return "";
    }

    return ",";
  };

  if (dvsaMileage) {
    dvsaMileage.forEach((dataEntry, i) => {
      if (dataEntry && dataEntry.completedDate) {
        dvsaString += `${dataEntry.mileage} (${formatDate(
          dataEntry.completedDate
        )})${getComma(i)} `;
      } else {
        dvsaString += `${dataEntry.mileage}${getComma(i)} `;
      }
    });
  }

  return (
    <ResponsiveLayout
      paddingY={0}
      display="flex"
      flexDirection={["column", "row"]}
      flexWrap={["wrap", "wrap", "nowrap", "nowrap"]}
    >
      <Box
        marginX={0}
        width="100%"
        display="flex"
        flexDirection="column"
        marginTop={[0, "44px"]}
      >
        <Specification
          fuel={fuel}
          doors={doors}
          color={color}
          keys={numberOfKeys}
          vehicleType={vehicleType}
          transmission={transmission}
          co2Emissions={co2Emissions}
          noxEmissions={noxEmissions}
          manufacturerColour={manufacturerColour}
        />
        <Ownership
          vin={vin}
          owners={owners}
          vatType={vatType}
          logBook={logBook}
          registrationDate={registrationDate}
        />
        <DeclarationSection
          usedAsTaxi={declaration?.usedAsTaxi}
          majorDamage={declaration?.majorDamage}
          insuranceTotalLoss={insuranceTotalLoss}
          reregistered={declaration?.reregistered}
          ownedByPolice={declaration?.ownedByPolice}
          modifiedVehicle={declaration?.modifiedVehicle}
          changeOfRegistration={declaration?.changeOfRegistration}
        />
        <EquipmentSection equipmentItems={equipmentItems} />
        <AdditionalInfoSection advisoryItems={advisoryItems} />
      </Box>
      <ServiceHistory
        notes={notes}
        dvsaMileage={dvsaString}
        serviceCount={serviceCount}
        MOTExpiryDate={MOTExpiryDate}
        serviceHistory={serviceHistory}
        lastServiceDate={lastServiceDate}
        lastServiceMileage={lastServiceMileage}
      />
    </ResponsiveLayout>
  );
};

export default VehicleSpecification;
